import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("psc_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("psc_token");
    if (!token) {
      setBooting(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("psc_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("psc_token");
        localStorage.removeItem("psc_user");
        setUser(null);
      })
      .finally(() => setBooting(false));
  }, []);

  const login = useCallback(async (email, password) => {
    // Auto-retry when the server is still waking up (network error / 5xx gateway)
    for (let attempt = 0; ; attempt++) {
      try {
        const r = await api.post("/auth/login", { email, password });
        if (r.data.mfa_required) {
          return { mfaRequired: true, mfaToken: r.data.mfa_token };
        }
        localStorage.setItem("psc_token", r.data.token);
        localStorage.setItem("psc_user", JSON.stringify(r.data.user));
        setUser(r.data.user);
        return { user: r.data.user, mfaSetupRequired: !!r.data.mfa_setup_required,
                 mustChangePassword: !!r.data.must_change_password };
      } catch (err) {
        const status = err?.response?.status;
        const retriable = !err.response || [502, 503, 504].includes(status);
        if (!retriable || attempt >= 2) throw err;
        await new Promise((res) => setTimeout(res, 2000 * (attempt + 1)));
      }
    }
  }, []);

  const verifyMfa = useCallback(async (mfaToken, code) => {
    const r = await api.post("/auth/mfa/login-verify", { mfa_token: mfaToken, code });
    localStorage.setItem("psc_token", r.data.token);
    localStorage.setItem("psc_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const register = useCallback(async (email, password, name, inviteToken, orgType) => {
    const payload = { email, password, name };
    if (orgType) payload.org_type = orgType;
    if (inviteToken) payload.invite_token = inviteToken;
    const r = await api.post("/auth/register", payload);
    localStorage.setItem("psc_token", r.data.token);
    localStorage.setItem("psc_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const registerV2 = useCallback(async (payload) => {
    const r = await api.post("/auth/register-v2", payload);
    localStorage.setItem("psc_token", r.data.token);
    localStorage.setItem("psc_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("psc_token");
    localStorage.removeItem("psc_user");
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("psc_user", JSON.stringify(r.data));
      return r.data;
    } catch { return null; }
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, verifyMfa, register, registerV2, logout, refresh, booting }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
